import { z } from 'zod'
import type { LowerHex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import {
  addressPrimitiveSchema,
  bigintTextSchema,
  lowerHexSchema,
} from '../../types/primitive'
import type { BlockscoutClient } from './client'

type GetInternalTransactionsByTransactionHashParameters = {
  client: BlockscoutClient
  txhash: LowerHex
  headers?: Record<string, string>
}

const internalTransactionSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  contractAddress: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  from: addressPrimitiveSchema,
  gas: bigintTextSchema,
  isError: z
    .string()
    .regex(/^\d+$/)
    .transform((x) => x === '1'),
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  to: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  transactionHash: lowerHexSchema,
  value: bigintTextSchema,
})

const resultSchema = z.array(internalTransactionSchema)

/** {@link https://docs.blockscout.com/developer-support/api/rpc-endpoints/account#get-internal-transactions-by-transaction-or-address-hash} */
export const getInternalTransactionsByTransactionHash = async ({
  client,
  txhash,
  headers,
}: GetInternalTransactionsByTransactionHashParameters): Promise<
  InternalTransactionWithIndex[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlistinternal',
        txhash,
      },
      resultSchema,
      headers,
    })
    return result.map((v) => ({
      blockNumber: v.blockNumber,
      contractAddress: v.contractAddress,
      from: v.from,
      gas: v.gas,
      isError: v.isError,
      timestamp: v.timeStamp * 1000,
      to: v.to,
      txHash: v.transactionHash,
      value: v.value,
    }))
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === 'No internal transactions found'
    ) {
      return []
    }
    throw error
  }
}
